'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { createClient } from '@/lib/supabase/client';

type UnreadNotificationOptions = {
  /** Called for every realtime INSERT so a caller can layer side effects (toasts, message-event dispatch). */
  onInsert?: (payload: unknown) => void;
};

/**
 * Unread count for the `notifications` table (never story_notifications).
 *
 * Mirrors the proven inline pattern in DashboardLayout: head-only exact count,
 * 60s interval refresh, the `notifications:unread-count` window event as an
 * external override, and a realtime channel for immediate badge updates. The
 * channel is named `shell-notifications-<uid>` so it can coexist with
 * DashboardLayout's own `notifications-<uid>` channel until that shell migrates
 * onto this hook.
 *
 * Returns `count: null` while the first read is in flight so callers can hide
 * the badge rather than flash a zero.
 */
export function useUnreadNotificationCount(
  userId: string | null | undefined,
  { onInsert }: UnreadNotificationOptions = {}
): { count: number | null; refresh: () => void } {
  const [count, setCount] = useState<number | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const onInsertRef = useRef(onInsert);
  onInsertRef.current = onInsert;

  const refresh = useCallback(() => setRefreshNonce((value) => value + 1), []);

  useEffect(() => {
    if (!userId) {
      setCount(null);
      return;
    }

    const supabase = createClient();
    let cancelled = false;

    const fetchCount = () => {
      supabase
        .from('notifications')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('read', false)
        .then(({ count: unread, error }) => {
          if (cancelled) return;
          if (error) {
            console.error('[notifications] count refresh failed', {
              code: error.code || 'unknown',
            });
          } else {
            setCount(unread || 0);
          }
        });
    };

    fetchCount();
    const interval = setInterval(fetchCount, 60000);

    const handleUnreadCount = (event: Event) => {
      const detail = (event as CustomEvent<number>).detail;
      if (typeof detail === 'number') setCount(Math.max(0, detail));
    };
    window.addEventListener('notifications:unread-count', handleUnreadCount);

    const channel = supabase
      .channel('shell-notifications-' + userId)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: 'user_id=eq.' + userId,
        },
        (payload) => {
          fetchCount();
          onInsertRef.current?.(payload.new);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: 'user_id=eq.' + userId,
        },
        () => fetchCount()
      )
      .subscribe();

    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener('notifications:unread-count', handleUnreadCount);
      supabase.removeChannel(channel);
    };
  }, [refreshNonce, userId]);

  return { count, refresh };
}
